import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// Create a simple event emitter
const toastEvents = {
  listeners: new Set(),
  emit: (type, message, duration) => {
    toastEvents.listeners.forEach(listener => listener(type, message, duration));
  },
  subscribe: (listener) => {
    toastEvents.listeners.add(listener);
    return () => toastEvents.listeners.delete(listener);
  }
};

// Global toast function that can be used anywhere
export const toast = {
  success: (message, duration = 3000) => toastEvents.emit('success', message, duration),
  error: (message, duration = 3000) => toastEvents.emit('error', message, duration),
  warning: (message, duration = 3000) => toastEvents.emit('warning', message, duration)
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  React.useEffect(() => {
    const addToast = (type, message, duration) => {
      const id = Math.random().toString(36).substr(2, 9);
      setToasts(prev => [...prev, { id, type, message }]);
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    };

    const unsubscribe = toastEvents.subscribe(addToast);
    return () => unsubscribe();
  }, []);

  return (
    <>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-start p-4 rounded-lg shadow-lg border transform translate-x-0 ${toast.type === 'success'
                ? 'bg-green-50 border-green-200'
                : toast.type === 'error'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {toast.type === 'success' && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {toast.type === 'error' && (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                {toast.type === 'warning' && (
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                )}
                <span
                  className={`font-medium ${toast.type === 'success'
                      ? 'text-green-800'
                      : toast.type === 'error'
                        ? 'text-red-800'
                        : 'text-yellow-800'
                    }`}
                >
                  {toast.type === 'success'
                    ? 'Thành công'
                    : toast.type === 'error'
                      ? 'Lỗi'
                      : 'Cảnh báo'}
                </span>
              </div>
              <p
                className={`mt-1 ${toast.type === 'success'
                    ? 'text-green-700'
                    : toast.type === 'error'
                      ? 'text-red-700'
                      : 'text-yellow-700'
                  }`}
              >
                {toast.message}
              </p>
            </div>
            <button
              onClick={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
              className="ml-4 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </>
  );
};